import {
    ApolloClient,
    ApolloLink,
    InMemoryCache,
    Observable,
    createHttpLink,
    from,
    split,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { apm } from '@elastic/apm-rum';
import { ApolloClients, DefaultApolloClient, provideApolloClients } from '@vue/apollo-composable';
import sha256 from 'crypto-js/sha256';
import { print } from 'graphql';
import { createClient } from 'graphql-ws';
import memoize from 'lodash/memoize';
import { storeToRefs } from 'pinia';
import type { App } from 'vue';
import { ref } from 'vue';
import { formatDate } from '@/core/lib/date';
import { getOddsApiUrl, getOddsApiWsUrl } from '@/core/lib/url';
import { isSupportedDeviceForWs } from '@/core/lib/utils';
import { OddApiStatus } from '@/interface/enum';
import { useOddsApiStore } from '@/store/oddsApiStore';
import { useToggleStore } from '@/store/toggleStore';

window.isUseTokenPrefix = false;

let unauthenticatedCount = 0;

// Matches V8 JSON.parse SyntaxError messages, e.g.
// "Expected ',' or '}' after property value in JSON at position 205"
// "Unexpected non-whitespace character after JSON at position 8"
// "Unexpected end of JSON input" is NOT matched (no position)
const JSON_PARSE_ERROR_PATTERN = /JSON at position (\d+)/;

const PARSE_ERROR_BODY_CONTEXT = 50;

const FULL_BODY_OPERATIONS = new Set(['BetBuilderEventExistsQuery']);

// Slices the body text around the position reported in the JSON.parse error message,
// so the bad character is visible regardless of how large the response is.
function getBodySnippetAroundError(bodyText: string, message: string, operationName: string): string {
    if (FULL_BODY_OPERATIONS.has(operationName)) return bodyText;

    const position = Number(JSON_PARSE_ERROR_PATTERN.exec(message)?.[1]);
    if (Number.isNaN(position)) return bodyText.slice(0, PARSE_ERROR_BODY_CONTEXT * 2);

    const start = Math.max(0, position - PARSE_ERROR_BODY_CONTEXT);
    const end = position + PARSE_ERROR_BODY_CONTEXT;
    const prefix = start > 0 ? '…' : '';
    const suffix = end < bodyText.length ? '…' : '';

    return `${prefix}${bodyText.slice(start, end)}${suffix}`;
}

// Attributes a non-JSON body or error status to its real producer: content-type / cache headers
// expose CDN or anti-bot interception, url + redirected expose challenge redirects, cf-ray pins the
// exact request in Cloudflare logs. Cross-origin fetch only exposes CORS-safelisted headers
// (content-type, …) plus whatever Access-Control-Expose-Headers allows.
function describeResponse(response: Response): string {
    if (!response) return '';

    const headers: string[] = [];
    response.headers.forEach((value, key) => headers.push(`${key}=${value}`));

    return `, url: ${response.url}, redirected: ${response.redirected}, headers: {${headers.join(', ')}}`;
}

const TOKEN_PREFIX = 'YplbcXy';

const tokenHeaderName = 'x-auth-token';

const startTime = new Date().getTime();
function getPageOpenedMinutes() {
    try {
        return `${~~(((new Date().getTime()) - startTime) / 1000 / 60)}m`;
    } catch (error) {
        console.error(error);
        return '--m';
    }
}

function debugLog(title: string, message: any) {
    const timeStamp = formatDate(new Date(), 'HH:mm:ss');
    console.warn(`[${timeStamp}][${getPageOpenedMinutes()}] ${title}: ${message}`);
}

const pendingResolves: Array<VoidFunction> = [];

export async function refreshAndScheduleAutoRefreshTokens() {
    const { refreshOddsApiTokens } = useOddsApiStore();

    try {
        await refreshOddsApiTokens();
        pendingResolves.forEach(req => req());

        registerNextAutoRefreshToken();
    } finally {
        pendingResolves.length = 0;
    }
}

const turnstileNoChallenge = memoize(() => Promise.resolve(true));

export type ChallengeStatus = 'NONE' | 'PENDING' | 'SUCCESS' | 'ERROR';

export const challengeStatus = ref<ChallengeStatus>('NONE');

const turnstileChallenge = memoize((): Promise<boolean> => {
    const { oddsApiUrl } = useToggleStore();
    const iframeUrl = `${getOddsApiUrl(oddsApiUrl)}/pong`;
    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', iframeUrl);
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    challengeStatus.value = 'PENDING';

    return new Promise((resolve, reject) => {
        const messageListener = ({ data = {} }: { data: any }) => {
            const { type, action } = data;

            if (type === 'challenge') {
                if (action === 'ready') {
                    iframe.contentWindow!.postMessage({ type: 'challenge', action: 'verify' }, '*');
                } else if (action === 'success') {
                    window.removeEventListener('message', messageListener);
                    challengeStatus.value = 'SUCCESS';
                    resolve(true);
                } else if (action === 'error') {
                    window.removeEventListener('message', messageListener);
                    challengeStatus.value = 'ERROR';
                    reject(new Error(data.error));
                }
            }
        };

        window.addEventListener('message', messageListener, false);
    });
});

let needChallenge = false;
function waitTurnstile() {
    return __ENABLE_TURNSTILE__ && needChallenge && navigator.onLine ? turnstileChallenge() : turnstileNoChallenge();
}

async function getOddsApiTokens() {
    const { authToken, oddsApiStatus } = storeToRefs(useOddsApiStore());

    const oddsApiTokenPromise = new Promise<string>((resolve) => {
        switch (oddsApiStatus.value) {
            case OddApiStatus.LOADED:
                resolve(authToken.value);
                break;
            case OddApiStatus.LOADING:
                pendingResolves.push(() => resolve(authToken.value));
                break;
            case OddApiStatus.NO_TOKEN:
                pendingResolves.push(() => resolve(authToken.value));
                refreshAndScheduleAutoRefreshTokens();
                break;
            case OddApiStatus.ERRORED:
            default:
                throw Error('GetOddsApiToken died');
        }
    });
    const [_token] = await Promise.all([oddsApiTokenPromise, waitTurnstile()]);

    const token = window.isUseTokenPrefix ? TOKEN_PREFIX + _token : _token;
    return token;
}

window.addEventListener('offline', () => {
    if (__ENV_PROD__) debugLog('network', 'offline');
});

// update token when user [disconnected] -> [connected]
window.addEventListener('online', () => {
    const { oddsApiStatus } = storeToRefs(useOddsApiStore());
    const { clearTokens } = useOddsApiStore();

    if (__ENV_PROD__) debugLog('network', 'online');

    if (oddsApiStatus.value === OddApiStatus.ERRORED) {
        clearTokens();
        refreshAndScheduleAutoRefreshTokens();
    }
});

// update token when the page [invisible] -> [visible]
window.addEventListener('visibilitychange', () => {
    const { oddsApiStatus } = storeToRefs(useOddsApiStore());
    const { clearTokens } = useOddsApiStore();

    if (__ENV_PROD__) debugLog('visibility', document.visibilityState);
    if (document.visibilityState === 'visible' && oddsApiStatus.value === OddApiStatus.ERRORED) {
        clearTokens();
        refreshAndScheduleAutoRefreshTokens();
    }
});

let refreshTokenTimeout: number;
/**
 * Register a timeout(10 mins) to refresh token before it expires (15 mins)
 */
function registerNextAutoRefreshToken() {
    window.clearTimeout(refreshTokenTimeout);

    refreshTokenTimeout = window.setTimeout(() => {
        refreshAndScheduleAutoRefreshTokens();
    }, 10 * 60 * 1000); // 10 minutes
}

export const isWsConnected = ref(false);

const apolloClients: Record<string, ApolloClient<any>> = {};
function createApolloClient({
    clientId,
    queryUrl,
    wsUrl,
    isEnableWebSocket,
}: {
    clientId: string;
    queryUrl: string;
    wsUrl: string;
    isEnableWebSocket: boolean;
}) {
    if (apolloClients[clientId]) return apolloClients[clientId];

    const authLink = setContext(async () => {
        const authToken = await getOddsApiTokens();

        return {
            headers: {
                [tokenHeaderName]: authToken,
            },
        };
    });

    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
        if (!__ENV_PROD__) {
            if (graphQLErrors) {
                graphQLErrors.forEach((error) => {
                    console.error(`[GraphQL error]: Message: ${error.message}`, error);
                });
            }
        }

        const { clearTokens } = useOddsApiStore();

        if (graphQLErrors) {
            const errorCode = graphQLErrors[0]?.extensions?.code;
            switch (errorCode) {
                case 'UNAUTHENTICATED':
                    debugLog('UNAUTHENTICATED', ++unauthenticatedCount);
                    clearTokens();
                    break;
                case 'BAD_ENCODE_INPUT':
                    window.isUseTokenPrefix = true;
                    break;
                case 'BAD_TOKEN_INPUT':
                    window.isUseTokenPrefix = false;
                    break;
                case 'FORBIDDEN':
                    break;
                default: {
                    const gqlMessage = graphQLErrors[0]?.message;
                    const enriched = new Error(`[${operation.operationName}] GraphQL error: [${errorCode}] - ${gqlMessage}, variables: ${JSON.stringify(operation.variables)}`);
                    apm.captureError(enriched);
                    break;
                }
            }
        }

        // networkError means
        // 1. network is down
        // 2. request is blocked by cloudflare
        //   - we have no way to verify if the request need to pass
        //     the turnstile challenge, so we assume it needs to.
        // 3. server error response (4xx/5xx)
        if (networkError) {
            if ('bodyText' in networkError && JSON_PARSE_ERROR_PATTERN.test(networkError.message)) {
                const snippet = getBodySnippetAroundError(networkError.bodyText, networkError.message, operation.operationName);
                const responseContext = describeResponse(networkError.response);
                const enriched = new Error(`[${operation.operationName}] Server parse error: HTTP ${networkError.statusCode}, ${networkError.message}, bodyLength: ${networkError.bodyText.length}, body: ${snippet}, variables: ${JSON.stringify(operation.variables)}${responseContext}`);
                apm.captureError(enriched);
            } else if ('statusCode' in networkError && networkError.statusCode >= 400 && networkError.statusCode !== 401) {
                const responseContext = describeResponse(networkError.response);
                const enriched = new Error(`[${operation.operationName}] Network error: HTTP ${networkError.statusCode}, variables: ${JSON.stringify(operation.variables)}${responseContext}`);
                apm.captureError(enriched);
            }

            if (__ENABLE_TURNSTILE__ && navigator.onLine) {
                needChallenge = true;
            }
        }

        return forward(operation);
    });

    const persistedQueryLink = createPersistedQueryLink({
        generateHash: document => sha256(print(document)).toString(),
        useGETForHashedQueries: true,
    });

    const httpLink = createHttpLink({
        uri: getOddsApiUrl(queryUrl),
        credentials: 'include',
    });

    const wsClient = createClient({
        url: getOddsApiWsUrl(wsUrl),
        connectionParams: async () => {
            const token = await getOddsApiTokens();
            // payload in connection_init
            return ({
                [tokenHeaderName]: token,
            });
        },
        shouldRetry: () => true,
        retryAttempts: Infinity,
        lazy: true,
        on: {
            error: () => {
                isWsConnected.value = false;
            },
            closed: () => {
                isWsConnected.value = false;
            },
            connecting: () => {
                isWsConnected.value = false;
            },
            connected: () => {
                isWsConnected.value = true;
            },
        },
    });

    const wsLink = split(
        () => !__MOCK_ODDS_API__ && isEnableWebSocket && !!wsUrl,
        new GraphQLWsLink(wsClient),
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        new ApolloLink(() => new Observable(() => {})),
    );

    const retryLink = new RetryLink({
        delay: {
            initial: 200,
            max: 1000,
            jitter: true,
        },
        attempts: {
            max: 3,
        // retryIf: (error, _operation) => !!error,
        },
    });

    const requestLink = __MOCK_ODDS_API__
        ? httpLink
        : from([
            persistedQueryLink,
            httpLink,
        ]);

    const terminateLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition'
            && definition.operation === 'subscription';
        },
        wsLink,
        requestLink,
    );

    const apolloClient = new ApolloClient({
        link: from([
            errorLink,
            retryLink,
            authLink,
            terminateLink,
        ]),
        cache: new InMemoryCache({
            addTypename: false,
        }),
        defaultOptions: {
            query: {
                fetchPolicy: 'no-cache',
            },
            watchQuery: {
                fetchPolicy: 'no-cache',
            },
        },
        connectToDevTools: __ENV_DEV__,
    });

    apolloClients[clientId] = apolloClient;

    return apolloClient;
}

export function setupApollo(app: App) {
    const { oddsApiUrl, oddsApiWsUrl } = useToggleStore();

    const isEnableWebSocket = isSupportedDeviceForWs();
    const oddsApiApolloClient = createApolloClient({
        clientId: 'oddsApi',
        queryUrl: `${oddsApiUrl}/api`,
        wsUrl: oddsApiWsUrl
            ? `${oddsApiWsUrl}/ws`
            : '',
        isEnableWebSocket,
    });

    provideApolloClients({
        default: oddsApiApolloClient,
        oddsApi: oddsApiApolloClient,
    });

    app.provide(DefaultApolloClient, oddsApiApolloClient);
    app.provide(ApolloClients, {
        default: oddsApiApolloClient,
        oddsApi: oddsApiApolloClient,
    });
}
